// type-only: matrix-js-sdk's main entry is ESM-only; a value import of EventType breaks Jest (see commit 6980edd20)
import type { EventType } from 'matrix-js-sdk';
import { KnownMembership, MatrixClient, Room } from 'matrix-js-sdk';
import { IMatrixRoomSummary } from 'shared-state/matrix-chat/types';

interface IMemberProfile {
    name: string;
    profilePic: string;
}

export const getDirectRoomIdSet = (client: MatrixClient): Set<string> => {
    const content = client.getAccountData('m.direct' as EventType.Direct)?.getContent() as Record<string, string[]> | undefined;
    if (!content) return new Set();
    return new Set(Object.values(content).flat());
};

const lastMessagePreview = (room: Room): string => {
    const events = room.getLiveTimeline().getEvents();
    const lastMessageEvent = [...events].reverse().find((event) => event.getType() === ('m.room.message' as EventType.RoomMessage));
    const body = lastMessageEvent?.getContent()?.body;
    return typeof body === 'string' ? body : '';
};

// Matrix room-member state can't be trusted to carry a proper displayname or avatar (either may
// be unset at invite/join time). Names are resolved from the org roster directory instead, keyed
// by matrix user id, falling back to Matrix's own calculated room name when a member (e.g.
// someone outside the org) can't be resolved.
//
// Avatars only get the org roster's profile picture here, as a synchronous fallback shown while
// the actual Matrix avatar resolves - that requires a live profile query plus an authenticated
// media fetch (see matrixProfile.ts / matrixMedia.ts), which needs a component's client access
// and can't happen inside this pure mapping function. otherUserId carries the other member's id
// for direct rooms so useMatrixUserAvatarUrl can resolve it.
export const mapRoomsToSummaries = (
    rooms: Room[],
    directRoomIds: Set<string>,
    myUserId: string,
    memberProfilesByMatrixId: Record<string, IMemberProfile>,
    // False until the org roster directory has completed its first load. Distinguishes "not
    // resolved because still loading" (show a placeholder) from "not resolved because this member
    // is genuinely outside the org roster" (permanently fall back to room.name) - see displayName.
    isDirectoryLoaded: boolean
): IMatrixRoomSummary[] => {
    return rooms
        .filter((room) => room.getMyMembership() === KnownMembership.Join)
        .map((room) => {
            const isDirect = directRoomIds.has(room.roomId);
            const otherMembers = room
                .getMembers()
                .filter((member) => (member.membership === KnownMembership.Join || member.membership === KnownMembership.Invite) && member.userId !== myUserId);
            const otherProfiles = otherMembers.map((member) => memberProfilesByMatrixId[member.userId]);
            const resolvedAll = otherProfiles.length > 0 && otherProfiles.every((profile): profile is IMemberProfile => Boolean(profile));

            let displayName: string | null;
            if (resolvedAll) {
                displayName = otherProfiles.map((profile) => profile.name).join(', ');
            } else if (!isDirectoryLoaded) {
                displayName = null;
            } else {
                // Room.name is often just the raw Matrix user ID when Matrix's own room-name
                // calculation has nothing better to go on - only an acceptable fallback once we know
                // the org directory genuinely can't resolve this member (not merely still loading).
                displayName = room.name;
            }

            return {
                roomId: room.roomId,
                type: isDirect ? ('direct' as const) : ('group' as const),
                displayName,
                avatarUrl: isDirect && resolvedAll ? otherProfiles[0].profilePic : undefined,
                otherUserId: isDirect && otherMembers.length === 1 ? otherMembers[0].userId : undefined,
                lastMessagePreview: lastMessagePreview(room),
                unreadCount: room.getUnreadNotificationCount()
            };
        });
};
